import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../utils/api';
import Wordmark from '../ui/Wordmark';
import { isNativeApp } from '../../utils/platform';

// Pre-launch curtain over the CLIENT marketplace, for the social-media
// announcement window. Enabled ONLY when VITE_COMING_SOON=1 is set at build
// time (Vercel Production). Without the var this whole component compiles to
// an inert null — merging it changes nothing until the owner flips the flag,
// and launch day is "delete the var + redeploy", no code change.
//
// WHAT IT COVERS — an allowlist of GATED routes, not exemptions. Everything
// not listed stays reachable, which is the safe direction while Petzy
// Business sits in App Review with petzy.ro links in its metadata: a missed
// exemption would 404 a reviewer; a missed gate merely previews a page early.
//   gated:  /  /search  /map  /salon/*   (anonymous web visitors only)
//   open:   legal pages, /contact, /blog, /login, /register, all of /pro
//           (salons keep onboarding — supply is the launch bottleneck)
//
// WHO GETS THROUGH:
//   • any signed-in user (the owner, salon staff, the odd early client) —
//     "Intră în cont" below is the owner's own access path
//   • the native apps — triple-safe: store binaries are built locally where
//     the Vercel var doesn't exist, isNativeApp guards at runtime anyway,
//     and App Store 2.1 would reject a "coming soon" shell
//
// Rendered as an OVERLAY (fixed, z-[80]) rather than a route swap, on purpose:
//   • z-[80] clears every piece of site chrome — AppHeader (z-50, whose Meniu
//     would otherwise poke through the curtain), BottomNav (z-40, z-[70] in
//     map mode) and the CookieBanner (z-50). Hiding the cookie banner here is
//     deliberate, not collateral: with no consent UI, no trackers load on the
//     curtain at all — legally clean, and the campaign's real metric is the
//     email signups below, not GA.
//   • the real page keeps rendering underneath, so Googlebot and the
//     link-preview pipeline keep seeing actual content during the window.

const ENABLED = import.meta.env.VITE_COMING_SOON === '1';

// Same key NewsletterPopup writes: someone who signs up here won't be nagged
// by the popup again after launch.
const STORAGE_SUBSCRIBED = 'petzy_newsletter_subscribed';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isGated = (pathname) =>
  pathname === '/'
  || pathname.startsWith('/search')
  || pathname.startsWith('/map')
  || pathname.startsWith('/salon/');

// Mounts its children one frame after itself, so the enter transition actually
// plays (mounting straight at opacity-100 would skip it). Used by the success
// view; plain CSS transitions, no animation library on the curtain.
function FadeIn({ children }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div className={`flex flex-col items-center transition-all duration-500 ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
      {children}
    </div>
  );
}

export default function ComingSoonGate() {
  const { pathname } = useLocation();
  const { user, loading } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  // form → sending → leaving (form fades out, 300ms) → done (success fades in).
  const [phase, setPhase] = useState('form');
  // One string per failure, never a shared flag: the first live signup failed
  // with the SERVER's "Numele este obligatoriu." (the backend requires a name
  // so campaign emails can greet people by first name) while the UI blamed the
  // email address. Show what actually went wrong.
  const [error, setError] = useState('')

  const active = ENABLED && !isNativeApp && !user && isGated(pathname);

  // Lock the page scroll while the curtain is up. `overflow: hidden` on body
  // is NOT enough: iOS Safari keeps letting touch scrolls chain through to the
  // page, and on desktop the window scrollbar kept moving behind the curtain —
  // harmless (the overlay is opaque, nothing is reachable) but it visibly gave
  // away the site underneath. The battle-tested lock is position:fixed on body
  // at the current scroll offset, restored on cleanup so a sign-in that lifts
  // the curtain lands back exactly where the page was.
  useEffect(() => {
    if (!active || loading) return undefined;
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
    };
  }, [active, loading]);

  if (!active) return null;
  // Auth still resolving: a blank cream sheet instead of either flash —
  // neither the marketplace (for a visitor) nor the curtain (for the owner).
  if (loading) return <div className="fixed inset-0 z-[80] bg-cream-50" aria-hidden="true" />;

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    // Mirror the backend's own checks (marketingService.subscribe: name >= 2
    // chars, email shape) so the common mistakes never leave the browser.
    if (trimmedName.length < 2) { setError('Spune-ne și numele tău (măcar 2 litere).'); return; }
    if (!EMAIL_RE.test(trimmedEmail)) { setError('Adresa de email nu pare validă.'); return; }
    setError('');
    setPhase('sending');
    try {
      // Backend clamps unknown sources to PLATFORM_POPUP, so these land in
      // the same audience the popup feeds — one launch list, no new plumbing.
      await api.post('/platform/subscribe', { email: trimmedEmail, name: trimmedName, source: 'PLATFORM_POPUP' });
      try { localStorage.setItem(STORAGE_SUBSCRIBED, 'true'); } catch { /* private mode */ }
      // Let the form fade fully before the success view mounts — the timeout
      // matches the duration-300 on the leaving block. A late setState after
      // an unmount is a safe no-op on React 18.
      setPhase('leaving');
      setTimeout(() => setPhase('done'), 320);
    } catch (err) {
      setPhase('form');
      setError(err?.response?.data?.message || 'N-a mers. Mai încearcă o dată, te rugăm.');
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col overflow-y-auto overscroll-none bg-cream-50">
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-16 text-center">
        <Wordmark className="text-4xl" />

        {phase === 'done' ? (
          // Succes: aceleași clase de titlu/subtitlu ca vederea de formular,
          // doar conținutul diferă — "100% integrat nativ" înseamnă zero
          // stiluri noi aici.
          <FadeIn>
            <h1 className="mt-10 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
              Super! Am notat adresa ta. 🐾
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-ink-500 sm:text-lg">
              Mulțumim pentru interes! Vei fi printre primii care află când dăm
              startul programărilor online pentru blănosul tău.
            </p>
          </FadeIn>
        ) : (
        <div className={`flex flex-col items-center transition-opacity duration-300 ${phase === 'leaving' ? 'opacity-0' : 'opacity-100'}`}>
        <h1 className="mt-10 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-5xl">
          Se lansează <span className="text-primary-500">în curând</span>
        </h1>
        <p className="mt-5 max-w-md text-base leading-relaxed text-ink-500 sm:text-lg">
          Programări online la saloane de toaletaj din orașul tău —
          fără telefoane, 24/7. Lasă-ne emailul și afli primul.
        </p>

          <form onSubmit={submit} className="mt-8 flex w-full max-w-md flex-col gap-3">
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              placeholder="Numele tău"
              aria-label="Numele tău"
              autoComplete="given-name"
              className="rounded-full bg-white px-5 py-3 text-sm text-ink ring-1 ring-ink/10 outline-none transition placeholder:text-ink-300 focus:ring-2 focus:ring-primary-500"
            />
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                placeholder="Adresa ta de email"
                aria-label="Adresa ta de email"
                autoComplete="email"
                className="flex-1 rounded-full bg-white px-5 py-3 text-sm text-ink ring-1 ring-ink/10 outline-none transition placeholder:text-ink-300 focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="submit"
                disabled={phase === 'sending' || phase === 'leaving'}
                className="rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-white transition hover:bg-primary-600 disabled:opacity-60"
              >
                {phase === 'sending' ? 'Se trimite…' : 'Anunță-mă'}
              </button>
            </div>
          </form>
          {error && (
            <p className="mt-3 text-sm text-red-600">{error}</p>
          )}
        </div>
        )}

        <p className="mt-10 text-sm text-ink-400">
          Ai un salon de toaletaj?{' '}
          <Link to="/pro" className="font-semibold text-primary-600 hover:underline">
            Descoperă Petzy Pro →
          </Link>
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-6 pb-8 text-xs text-ink-300">
        <Link to="/terms" className="hover:text-ink-500">Termeni</Link>
        <Link to="/privacy" className="hover:text-ink-500">Confidențialitate</Link>
        <Link to="/contact" className="hover:text-ink-500">Contact</Link>
        {/* The owner's (and salon staff's) way in: signing in lifts the gate. */}
        <Link to="/login" className="hover:text-ink-500">Intră în cont</Link>
      </div>
    </div>
  );
}
